import React, {useEffect, useRef} from 'react';
import {createPortal} from 'react-dom';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ArrowsClockwise, CheckCircle, Circle, PencilSimple, Warning, X} from '../../icons';
import {ERROR_KIND_GENERIC, ERROR_KIND_PERMISSIONS, resolveErrorKind} from '../../channels/adAccountErrorKinds';
import {formatShortDate, isPaidReconnectChannel, ownerSentence} from '../connections/reconnectAccountText';
import AdAccountBudgetWithDetails from '../AdAccountBudgetWithDetails';
import {EditClientAdAccountModal} from '../../client/ad_accounts/EditClientAdAccountModal';
import {DAILY_LIMIT_TYPE_DAILY_CAMPAIGN_ONLY_BUDGET, DAILY_LIMIT_TYPE_DATA, getLocalizedShortText} from '../../../daily_limits';
import {platformName} from '../../../utils/filterNotes';
import {
    KIND_ACCOUNT,
    KIND_PROFITABILITY,
    dailyLimitOf,
    hasAccess,
    integrationStatusOf,
    maintenanceKindOf,
    reconnectKindOf,
} from './accountsModel';
import {AccountLogo, LimitCell, reconnectLabel} from './AccountParts';
import {ClientAvatar, ClientPickerButton} from './ClientPicker';

// Lo marca el `<body>` mientras el drawer está abierto: los banners se corren y el chat y el
// lanzador de feedback se ocultan (`_agency-accounts-v2.scss`), como con `LineEditDrawer`.
export const DRAWER_BODY_CLASS = 'aac-drawer-open';

// Las integraciones que la fila clásica muestra como íconos con tooltip (`ClientAdAccountRow`). Son
// INFORMACIÓN —dónde están disponibles los datos de la cuenta—, no acciones: el drawer las conserva
// como lista, con el mismo texto que el tooltip de siempre cuando no están activas.
const INTEGRATIONS = [
    {key: 'looker', labelKey: 'client_ad_account_stores_list.looker_studio', prefix: 'looker_cc'},
    {key: 'reports', labelKey: 'layout.informes', prefix: 'data_node'},
    {key: 'mcp', labelKey: 'client_ad_account_stores_list.mcp', prefix: 'mcp_status'},
];

function integrationNote(t, raw, status, prefix) {
    if (status === 'active') return null;
    if (status === 'processing') {
        const percentage = (raw.looker_cc_status_data && raw.looker_cc_status_data.completed_percentage) || 0;
        return t(`${prefix}.processing_tooltip`, {percentage});
    }
    return t(`${prefix}.${raw.has_connection ? 'not_included_tooltip' : 'not_included_or_not_connected'}`);
}

/**
 * El detalle de una cuenta conectada (contrato §3). Conserva lo que la fila clásica tenía —el
 * presupuesto de la plataforma con su modal por campaña, el límite diario con su formulario de
 * siempre, las integraciones y desconectar— y suma el cliente con «Cambiar de cliente».
 *
 * Se portea al `<body>`: la pantalla es un `@container` (layout containment), y un `fixed` adentro
 * se posicionaría contra ella y no contra la ventana.
 */
// Lo que el Tab puede alcanzar adentro del drawer.
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), '
    + 'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function AccountDrawer({row, clients, clientsState, onLoadClients, linkedClientIds = [], onChangeClient,
                                  onCreateClient, onRepair, onDisconnect, onEdited, onClose}) {
    const {t, i18n} = useTranslation();
    const closeRef = useRef(null);
    const asideRef = useRef(null);
    const editModalRef = useRef(null);

    useEffect(() => {
        const opener = document.activeElement;
        document.body.classList.add(DRAWER_BODY_CLASS);
        if (closeRef.current) closeRef.current.focus();
        return () => {
            document.body.classList.remove(DRAWER_BODY_CLASS);
            if (opener && opener.focus && document.body.contains(opener)) opener.focus();
        };
    }, []);

    // Escape cierra el drawer, salvo que haya un modal encima (el del límite, el de reconectar, la
    // confirmación): ése se cierra solo y el drawer tiene que seguir ahí.
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key !== 'Escape') return;
            if (document.body.classList.contains('modal-open')) return;
            onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    // `aria-modal` promete que el foco no sale del drawer (review C-10): el Tab da la vuelta adentro
    // del `<aside>` en vez de pasar a las filas de detrás del velo.
    const handleTabTrap = (event) => {
        if (event.key !== 'Tab' || !asideRef.current) return;
        const focusable = Array.from(asideRef.current.querySelectorAll(FOCUSABLE));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    const raw = row.raw || {};
    const clientName = row.clientName;
    const limit = dailyLimitOf(row);
    const ok = hasAccess(row);
    const reconnectKind = reconnectKindOf(row);
    const maintenanceKind = maintenanceKindOf(row);
    const nameOpts = {client: clientName, interpolation: {escapeValue: false}};
    // La fecha viene formateada del backend («04/10/2026 10:02»): sin esto, las barras salen como
    // `&#x2F;` (i18next escapa los valores interpolados; ver el CLAUDE.md del repo).
    const whenOpts = {when: raw.last_access_check, interpolation: {escapeValue: false}};

    const renderAccess = () => {
        if (ok) {
            return (
                <div className="aac-dr-line">
                    <span className="aac-access -ok">
                        <CheckCircle size={16} weight="bold" aria-hidden="true"/>
                        {t('agency_v2.accounts.access.ok')}
                    </span>
                    {raw.last_access_check && (
                        <span className="aac-muted">{t('agency_v2.accounts.detail.access_ok', whenOpts)}</span>
                    )}
                    {/* Al día, una planilla igual se edita y una tienda se vuelve a autorizar, como en
                        el clásico (review C-04). */}
                    {maintenanceKind && (
                        <>
                            <span className="aac-spacer"/>
                            <button type="button" className="aac-btn -sm" onClick={() => onRepair(row, maintenanceKind)}>
                                {maintenanceKind === 'sheet'
                                    ? <PencilSimple size={13} weight="bold" aria-hidden="true"/>
                                    : <ArrowsClockwise size={13} weight="bold" aria-hidden="true"/>}
                                {maintenanceKind === 'sheet'
                                    ? t('clients.edit_sheet_button')
                                    : t('agency_v2.accounts.detail.reauthorize_store')}
                            </button>
                        </>
                    )}
                </div>
            );
        }
        // En las cuentas de pauta (UD-20, M4): «Sin acceso desde el 23/10, 16:04. La ve el usuario de Meta
        // Cami. Hasta que la reconectes, sus datos no se actualizan.» La fecha sale del ISO; un backend
        // viejo no lo manda y queda el texto de antes, arriba.
        const paid = isPaidReconnectChannel(row.channel);
        const errorKind = resolveErrorKind(raw.error_kind);
        const lostSince = paid ? formatShortDate(raw.no_access_since_at, i18n && i18n.language) : null;
        const paidText = paid ? [
            lostSince ? t('agency_v2.accounts.detail.no_access_since', {date: lostSince, interpolation: {escapeValue: false}}) : null,
            ownerSentence(t, row.channel, raw),
            t('agency_v2.accounts.detail.access_error_text'),
        ].filter(Boolean).join(' ') : null;
        const showsKindReason = !paid || (errorKind !== ERROR_KIND_PERMISSIONS && errorKind !== ERROR_KIND_GENERIC);
        return (
            <>
                <div className="aac-dr-line">
                    <span className="aac-access -err">
                        <Warning size={16} weight="bold" aria-hidden="true"/>
                        {t('agency_v2.accounts.access.error')}
                    </span>
                    {!lostSince && raw.no_access_since && (
                        <span className="aac-muted">{t('clients.no_access_since')} {raw.no_access_since}</span>
                    )}
                    <span className="aac-spacer"/>
                    {reconnectKind && (
                        <button type="button" className="aac-btn -sm -primary" onClick={() => onRepair(row, reconnectKind)}>
                            <ArrowsClockwise size={13} weight="bold" aria-hidden="true"/>
                            {reconnectLabel(t, row)}
                        </button>
                    )}
                </div>
                {/* El motivo sale del `error_kind` traducido, como en la fila clásica: el texto del
                    backend no está pensado para esta pantalla. En pauta, «perdimos el acceso» lo dice
                    la frase de abajo, con la fecha y quién la ve. */}
                {showsKindReason && <p className="aac-dr-text">{t(`reconnect_modal.error_kind.${errorKind}`)}</p>}
                <p className="aac-dr-text" data-testid="aac-dr-access-text">
                    {paid ? paidText : t('agency_v2.accounts.detail.access_error_text')}
                </p>
                {raw.last_access_check && (
                    <p className="aac-dr-text -muted">{t('agency_v2.accounts.detail.access_ok', whenOpts)}</p>
                )}
            </>
        );
    };

    // Sin acceso, el presupuesto sólo diría «[Sin acceso]», que el bloque de arriba ya explica.
    const showsBudget = row.kind === KIND_ACCOUNT && ok && raw.channel_has_campaigns_with_budgets === true;
    const showsLimit = limit.state !== 'na';
    // El aviso de límite diario existe para los canales que lo manejan (`DAILY_LIMIT_EXCEEDED`). Su
    // texto habla de GASTO: el único tipo que no mira el gasto (sólo el presupuesto de las
    // campañas) no lo lleva.
    const showsLimitHint = showsLimit && limit.type !== DAILY_LIMIT_TYPE_DAILY_CAMPAIGN_ONLY_BUDGET;
    const exceededToday = showsLimit && !!raw.daily_limit_last_exceed_notification_is_today;

    return createPortal(
        <>
            <div className="aac-scrim" onClick={onClose} data-testid="aac-scrim"/>
            <aside ref={asideRef} className="aac-drawer" role="dialog" aria-modal="true" onKeyDown={handleTabTrap}
                   aria-label={t('agency_v2.accounts.detail.label')} data-testid="aac-drawer">
                <header className="aac-dr-head">
                    <AccountLogo channel={row.channel} size={40}/>
                    <span className="aac-dr-head__tx">
                        <b className="aac-dr-head__name">{row.name || row.accountId}</b>
                        <span className="aac-dr-head__sub">
                            {platformName(row.channel)}{row.accountId ? ` · ${row.accountId}` : ''}
                        </span>
                    </span>
                    <button ref={closeRef} type="button" className="aac-icon-btn"
                            aria-label={t('agency_v2.accounts.detail.close')} onClick={onClose}>
                        <X size={16} weight="bold" aria-hidden="true"/>
                    </button>
                </header>

                <div className="aac-dr-body">
                    <section className="aac-dr-sec">
                        <h3 className="aac-dr-t">{t('agency_v2.accounts.detail.client')}</h3>
                        <div className="aac-dr-line">
                            <span className="aac-client-chip">
                                <ClientAvatar name={clientName}/>
                                {clientName}
                            </span>
                            <span className="aac-spacer"/>
                            {/* Escalafy no se cambia de cliente desde acá: el backend no deja una
                                organización en dos clientes, y «conectar al nuevo y después
                                desconectar del anterior» (el orden que no pierde nada si algo falla)
                                rebotaría siempre en el primer paso. */}
                            {row.kind !== KIND_PROFITABILITY && (
                                <ClientPickerButton label={t('agency_v2.accounts.detail.change_client')}
                                                    clients={clients} clientsState={clientsState}
                                                    onOpen={onLoadClients} suggested={null}
                                                    excludeIds={linkedClientIds.concat([row.clientId])}
                                                    onPick={(client) => onChangeClient(row, client)}
                                                    onCreate={() => onCreateClient(row)}/>
                            )}
                        </div>
                    </section>

                    <section className="aac-dr-sec">
                        <h3 className="aac-dr-t">{t('agency_v2.accounts.detail.access')}</h3>
                        {renderAccess()}
                        <Link className="aac-link" to="/agency/conections">{t('agency_v2.accounts.detail.see_platforms')}</Link>
                    </section>

                    {showsBudget && (
                        <section className="aac-dr-sec">
                            <h3 className="aac-dr-t">{t('agency_v2.accounts.detail.platform_budget')}</h3>
                            <div className="aac-dr-budget">
                                <AdAccountBudgetWithDetails currency={raw.currency_code}
                                                            dailyBudget={raw.daily_budget}
                                                            channelHasBudgets={raw.channel_has_campaigns_with_budgets}
                                                            dailyBudgetUpdatedOn={raw.last_campaigns_sync}
                                                            campaignsLastUpdate={raw.campaigns_last_update}
                                                            hasAccess={raw.has_access}
                                                            hasInternalProblem={raw.has_internal_problem}
                                                            maxCampaignDailyBudget={raw.max_campaign_daily_budget}
                                                            adAccountId={raw.ad_account_id}/>
                            </div>
                        </section>
                    )}

                    {showsLimit && (
                        <section className="aac-dr-sec">
                            <h3 className="aac-dr-t">{t('agency_v2.accounts.detail.daily_limit')}</h3>
                            <div className="aac-dr-line">
                                <LimitCell row={row}/>
                                {limit.state === 'set' && DAILY_LIMIT_TYPE_DATA[limit.type] && (
                                    <span className="aac-muted">
                                        {getLocalizedShortText(DAILY_LIMIT_TYPE_DATA[limit.type], i18n.language)}
                                    </span>
                                )}
                                {exceededToday && (
                                    <span className="aac-flag">{t('clients.limit_exceeded')}</span>
                                )}
                                <span className="aac-spacer"/>
                                <button type="button" className="aac-btn -sm"
                                        onClick={() => editModalRef.current && editModalRef.current.toggle()}>
                                    {t('agency_v2.accounts.detail.edit')}
                                </button>
                            </div>
                            {showsLimitHint && <p className="aac-dr-text -muted">{t('agency_v2.accounts.detail.daily_limit_hint')}</p>}
                            {/* El formulario del límite de siempre: duplicarlo daría dos lugares donde
                                arreglar el mismo bug. */}
                            <EditClientAdAccountModal ref={editModalRef}
                                                      title={t('edit_client_ad_account_modal.title')}
                                                      clientAdAccount={raw}
                                                      clientId={row.clientId}
                                                      clientName={clientName}
                                                      afterSuccessfulEdited={onEdited}/>
                        </section>
                    )}

                    {row.kind !== KIND_PROFITABILITY && (
                        <section className="aac-dr-sec">
                            <h3 className="aac-dr-t">{t('agency_v2.accounts.detail.integrations')}</h3>
                            <ul className="aac-int-list">
                                {INTEGRATIONS.map((integration) => {
                                    const status = integrationStatusOf(raw);
                                    const note = integrationNote(t, raw, status, integration.prefix);
                                    return (
                                        <li key={integration.key} className={`aac-int -${status}`}>
                                            {status === 'active'
                                                ? <CheckCircle size={15} weight="bold" aria-hidden="true"/>
                                                : <Circle size={15} weight="bold" aria-hidden="true"/>}
                                            <span className="aac-int__tx">
                                                <span className="aac-int__name">{t(integration.labelKey)}</span>
                                                {note && <span className="aac-int__note">{note}</span>}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </section>
                    )}
                </div>

                <footer className="aac-dr-foot">
                    <button type="button" className="aac-btn -danger" onClick={() => onDisconnect(row)}>
                        {t('agency_v2.accounts.detail.disconnect', nameOpts)}
                    </button>
                    {row.kind !== KIND_PROFITABILITY && (
                        <p className="aac-dr-text -muted">{t('agency_v2.accounts.detail.disconnect_hint')}</p>
                    )}
                </footer>
            </aside>
        </>,
        document.body,
    );
}
